<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, computed } from 'vue'
import { setStoreLogger } from '../../logger'
import { GetImageRootUrl, GetEventListUrl } from '../../stores/SettingsStore'
import { FormatCurrency } from '../../services/currencyFormatter'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faUsers, faMoneyBillWave, faShoppingBag, faAward, faChartLine, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons'
import { DynamicReportExecute } from '../../services/CommonService'
import { EventOpenGetList } from '../../services/EventService'
import { handleError } from '../../services/ErrorService'
import type { IConsultantProgressResponse, IConsultantProgress, IPromotionProgressResponse, IPromotionProgress } from '../../types/ConsultantTypes'
import { ProgressBar } from '@progress/kendo-vue-progressbars'

const props = defineProps<{
  reportid: string
  trainingvideourl: string
}>()

const logger = useLogger()
setStoreLogger(logger)
logger.debug('AOEDashboardProgress.vue')

const imageRootUrl = ref<string>('')
const isLoading = ref(true)
const progressData = ref<IConsultantProgress | null>(null)
const promotionProgressData = ref<IPromotionProgress[]>([])
const isLoadingPromotion = ref(true)
const activePromotionSlide = ref(0)

// Computed properties based on progress data
const currentLevel = computed(() => progressData.value?.currentLevel)
const nextLevel = computed(() => progressData.value?.nextLevel)
const careerRecruits = computed(() => progressData.value?.careerRecruits)
const careerSales = computed(() => progressData.value?.careerPersCommVol || 0)
const monthlyQualifyingVolume = computed(() => progressData.value?.monthlyQualifyingVolume || 0)
const monthlySales = computed(() => progressData.value?.sales || 0)
const personalRecruits = computed(() => progressData.value?.personalRecruits || 0)
const monthlyBaseRate = computed(() => progressData.value?.monthlyBaseRate || 0)
const pdfTitle = computed(() => progressData.value?.pdfTitle)

// Derived values
const unsubmittedPartyOrders = ref<number>(0)
const unsubmittedMonthlyOrders = ref<number>(0)
const unsubmittedOrdersCount = ref<number>(0)
const unsubmittedMonthlyOrdersCount = ref<number>(0)

// Current month name for display
const currentMonthName = computed(() => {
  const date = new Date()
  return t(`months.${date.getMonth()}`) // This assumes you have month names in your translations
})

onMounted(async () => {
  logger.debug('onMounted')

  imageRootUrl.value = await GetImageRootUrl()

  GetProgressData()
  GetPromotionProgressData()
  GetOpenEventList()
})

function GetOpenEventList()
{
  EventOpenGetList().then((eventResp) => {
    if (eventResp)
    {
      const thisMonthEvents = eventResp.items.filter(event => new Date(event.eventStartDate).getMonth() === new Date().getMonth() && new Date(event.eventStartDate).getFullYear() === new Date().getFullYear())

      unsubmittedOrdersCount.value = eventResp.items.length
      unsubmittedMonthlyOrdersCount.value = thisMonthEvents.length
      unsubmittedPartyOrders.value = eventResp.items.reduce((total, event) => total + (event.cvEarnedTotal || 0), 0)
      unsubmittedMonthlyOrders.value = thisMonthEvents.reduce((total, event) => total + (event.cvEarnedTotal || 0), 0)
    }
  }).catch(async error => {
    await handleError(error, t)
  })
}

function CareerPlanClickHandler() {
  if (pdfTitle.value) {
    window.open(imageRootUrl.value + '/contentimages/' + pdfTitle.value, '_blank')
  }
}

function MyPartiesClickHandler() {
  logger.debug('My Parties clicked')
  GetEventListUrl().then(url => {
    window.location.href = url
  })
}

function MyTeamClickHandler() {
  // Implement my reports navigation
  logger.debug('Team Overview clicked')
  // Navigate to reports page
}

function TrainingVideosClickHandler() {
  if (props.trainingvideourl.length > 0) {
    window.open(props.trainingvideourl, '_blank')
  }
}

function GetProgressData() {
  isLoading.value = true

  // Using the same pattern as in AoeDashboardIncentivesWithLevels
  DynamicReportExecute({ reportId: props.reportid })
    .then((response: IConsultantProgressResponse) => {
      if (response && response.Items && response.Items.length > 0) {
        progressData.value = response.Items[0] // Get the first item
        logger.debug('Progress data loaded', progressData.value)
      } else {
        logger.error('No progress data returned')
      }
    })
    .catch(error => {
      logger.error('Error fetching progress data', error)
    })
    .finally(() => {
      isLoading.value = false
    })
}

function GetPromotionProgressData() {
  isLoadingPromotion.value = true

  DynamicReportExecute({ reportId: 'PROMOTIONPROGRESS' })
    .then((response: IPromotionProgressResponse) => {
      if (response && response.Items && response.Items.length > 0) {
        // Convert string amounts to numbers and sort by OrderId
        promotionProgressData.value = response.Items.map(item => ({
          ...item,
          CurrentAmount: typeof item.CurrentAmount === 'string' ? parseFloat(item.CurrentAmount) : item.CurrentAmount,
          GoalAmount: typeof item.GoalAmount === 'string' ? parseFloat(item.GoalAmount) : item.GoalAmount,
          OrderId: typeof item.OrderId === 'string' ? parseInt(item.OrderId) : item.OrderId
        })).sort((a, b) => a.OrderId - b.OrderId)
        logger.debug('Promotion progress data loaded', promotionProgressData.value)
      } else {
        logger.error('No promotion progress data returned')
      }
    })
    .catch(error => {
      logger.error('Error fetching promotion progress data', error)
    })
    .finally(() => {
      isLoadingPromotion.value = false
    })
}

// Helper functions to format individual requirement data
function formatAmount(amount: number, type: string): string {
  if (type === 'CURRENCY') {
    return FormatCurrency(amount, null) || '0'
  }
  // PQP, TQP, OQP, and INT types - format as number with decimals if needed
  if (type === 'PQP' || type === 'TQP' || type === 'OQP') {
    // Show decimals only if the number has a fractional part
    return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)
  }
  // INT type - always show as whole number
  return Math.round(amount).toString()
}

// Format amount with type suffix for progress message (e.g., "350 PQP")
function formatAmountWithSuffix(amount: number, type: string): string {
  const formattedAmount = formatAmount(amount, type)
  if (type === 'PQP' || type === 'TQP' || type === 'OQP') {
    return `${formattedAmount} ${type}`
  }
  return formattedAmount
}

// Get type suffix for progress amounts display
function getTypeSuffix(type: string): string {
  if (type === 'PQP' || type === 'TQP' || type === 'OQP') {
    return ` ${type}`
  }
  return ''
}

function calculatePercentage(current: number, goal: number): number {
  if (goal === 0) return 0
  const percentage = (current / goal) * 100
  return percentage > 100 ? 100 : percentage
}

const isCurrentRequirementCompleted = computed(() => {
  if (!promotionProgressData.value.length || activePromotionSlide.value >= promotionProgressData.value.length) {
    return false
  }
  const requirement = promotionProgressData.value[activePromotionSlide.value]
  return requirement.CurrentAmount >= requirement.GoalAmount
})
</script>

<template>
  <div class="dashboard-container">
    <div class="dashboard-row">
      <!-- Left Column - My Progress -->
      <div class="dashboard-card">
        <div class="card-header">
          <div class="card-title">{{ t('dashboard.progress.MyProgress') }} <span class="team-badge">{{ currentLevel }}</span></div>
          <p class="card-subtitle">{{ t('dashboard.progress.ProgressInfo') }}</p>
        </div>

        <div class="card-content" :class="{ 'is-loading': isLoading }">
          <!-- Total Recruitment Count -->
          <div class="info-row">
            <div class="info-icon">
              <FontAwesomeIcon :icon="faUsers" class="icon users-icon" />
            </div>
            <div class="info-label">
              {{ t('dashboard.progress.TotalRecruitmentCount') }}
            </div>
            <div class="info-value">
              {{ careerRecruits }}
            </div>
          </div>

          <!-- Career Sales -->
          <div class="info-row">
            <div class="info-icon">
              <FontAwesomeIcon :icon="faMoneyBillWave" class="icon money-icon" />
            </div>
            <div class="info-label">
              {{ t('dashboard.progress.CareerSales') }}
            </div>
            <div class="info-value">
              {{ FormatCurrency(careerSales, null) }}
            </div>
          </div>

          <!-- Unsubmitted Party Orders -->
          <div class="info-row">
            <div class="info-icon">
              <FontAwesomeIcon :icon="faShoppingBag" class="icon shopping-icon" />
            </div>
            <div class="info-label">
              {{ t('dashboard.progress.UnsubmittedPartyOrder') }}
              <span class="count-badge">{{ unsubmittedOrdersCount }}</span>
            </div>
            <div class="info-value">
              {{ FormatCurrency(unsubmittedPartyOrders, null) }}
            </div>
          </div>

          <!-- Next Promotion Level -->
          <div class="info-row">
            <div class="info-icon">
              <FontAwesomeIcon :icon="faAward" class="icon award-icon" />
            </div>
            <div class="info-label">
              {{ t('dashboard.progress.NextPromotionLevel') }}
              <span v-if="nextLevel" class="promotion-level">{{ nextLevel }}</span>
            </div>
            <div class="info-value">
              {{ nextLevel }}
            </div>
          </div>

          <!-- Promotion Progress Section - Carousel -->
          <div v-if="promotionProgressData.length > 0 && !isLoadingPromotion" class="promotion-progress-container">
            <div class="promotion-progress-section">
              <div class="promotion-requirement-label">
                {{ t('dashboard.progress.' + promotionProgressData[activePromotionSlide].RequirementId) }}
              </div>
              
              <!-- In Progress Message -->
              <div v-if="!isCurrentRequirementCompleted" class="promotion-progress-message">
                <span v-html="t('dashboard.progress.' + promotionProgressData[activePromotionSlide].RequirementProgressMessage, [ 
                  '<strong>' + formatAmountWithSuffix(
                    promotionProgressData[activePromotionSlide].GoalAmount - promotionProgressData[activePromotionSlide].CurrentAmount, 
                    promotionProgressData[activePromotionSlide].RequirementType
                  ) + '</strong>' 
                ])"></span>
              </div>
              
              <!-- Completed Message -->
              <div v-else class="promotion-progress-completed">
                {{ t('dashboard.progress.RequirementCompleted') }}
              </div>
              
              <div class="promotion-progress-bar">
                <ProgressBar
                  :value="calculatePercentage(promotionProgressData[activePromotionSlide].CurrentAmount, promotionProgressData[activePromotionSlide].GoalAmount)"
                  :labelVisible="false"
                />
              </div>
              <div class="promotion-progress-amounts">
                {{ formatAmount(promotionProgressData[activePromotionSlide].CurrentAmount, promotionProgressData[activePromotionSlide].RequirementType) + '/' + formatAmount(promotionProgressData[activePromotionSlide].GoalAmount, promotionProgressData[activePromotionSlide].RequirementType) + getTypeSuffix(promotionProgressData[activePromotionSlide].RequirementType) }}
              </div>
            </div>

            <!-- Carousel Indicators -->
            <div v-if="promotionProgressData.length > 1" class="promotion-carousel-indicators">
              <span
                v-for="(_, index) in promotionProgressData.length"
                :key="index"
                :class="['promotion-indicator', { active: activePromotionSlide === index }]"
                @click="activePromotionSlide = index"
              ></span>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <button @click="CareerPlanClickHandler" class="baseBtn secondaryBtn dashboard-career-plan-button">
            <FontAwesomeIcon :icon="faAward" class="button-icon" />
            {{ t('buttons.CareerPlan') }}
          </button>
          <button @click="MyPartiesClickHandler" class="baseBtn primaryBtn dashboard-my-parties-button">
            {{ t('buttons.MyParties') }}
          </button>
        </div>
      </div>

      <!-- Right Column - Current Month Overview -->
      <div class="dashboard-card">
        <div class="card-header">
          <div class="card-title">{{ currentMonthName }} {{ t('dashboard.progress.Overview') }}</div>
          <p class="card-subtitle">{{ t('dashboard.progress.MonthlyProgressInfo') }}</p>
        </div>

        <div class="card-content" :class="{ 'is-loading': isLoading }">
          <!-- Monthly Recruitment Count -->
          <div class="info-row">
            <div class="info-icon">
              <FontAwesomeIcon :icon="faUsers" class="icon users-icon" />
            </div>
            <div class="info-label">
              {{ t('dashboard.progress.MonthlyRecruitmentCount') }}
            </div>
            <div class="info-value">
              {{ personalRecruits }}
            </div>
          </div>

          <!-- Monthly Revenue -->
          <div class="info-row">
            <div class="info-icon">
              <FontAwesomeIcon :icon="faMoneyBillWave" class="icon money-icon" />
            </div>
            <div class="info-label">
              {{ t('dashboard.progress.MonthlySales') }}
            </div>
            <div class="info-value">
              {{ FormatCurrency(monthlySales, null) }}
            </div>
          </div>

          <!-- Monthly Qualifying Volume -->
          <div class="info-row">
            <div class="info-icon">
              <FontAwesomeIcon :icon="faChartLine" class="icon chart-icon" />
            </div>
            <div class="info-label">
              {{ t('dashboard.progress.MonthlyQV') }}
            </div>
            <div class="info-value">
              {{ monthlyQualifyingVolume }} {{ t('dashboard.progress.QV') }}
            </div>
          </div>

          <!-- Unsubmitted Party Orders -->
          <div class="info-row">
            <div class="info-icon">
              <FontAwesomeIcon :icon="faShoppingBag" class="icon shopping-icon" />
            </div>
            <div class="info-label">
              {{ t('dashboard.progress.UnsubmittedPartyOrder') }}
              <span class="count-badge">{{ unsubmittedMonthlyOrdersCount }}</span>
            </div>
            <div class="info-value">
              {{ FormatCurrency(unsubmittedMonthlyOrders, null) }}
            </div>
          </div>

          <!-- Commission Rate -->
          <div class="info-row">
            <div class="info-icon">
              <FontAwesomeIcon :icon="faFileInvoiceDollar" class="icon invoice-icon" />
            </div>
            <div class="info-label">
              {{ t('dashboard.progress.CommissionRate') }}
            </div>
            <div class="info-value">
              {{ monthlyBaseRate + '%' }}
            </div>
          </div>
        </div>

        <div class="card-footer">
          <button
            v-if="props.trainingvideourl.length > 0"
            @click="TrainingVideosClickHandler" class="baseBtn secondaryBtn dashboard-training-videos-button">
            {{ t('buttons.TrainingVideos') }}
          </button>
          <button @click="MyTeamClickHandler" class="baseBtn primaryBtn dashboard-my-teams-button">
            {{ t('buttons.MyTeam') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dashboard-container {
  width: 100%;
}

.dashboard-row {
  display: flex;
  gap: $spacing-5;
  flex-wrap: wrap;
}

/* Card styles */
.dashboard-card {
  background-color: white;
  border-radius: $radius-md;
  box-shadow: 0 2px 8px rgba(black, 0.1);
  flex: 1;
  min-width: 300px;
  display: flex;
  flex-direction: column;
}

/* Card header styles */
.card-header {
  padding: $spacing-5 $spacing-5 $spacing-2-5;
}

.card-title {
  @include typography-display;
  @include font-weight-semibold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.team-badge {
  @include typography-body;
  background-color: #e9f5f7;
  color: var(--vt-c-teal-dark);
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-lg;
  margin-left: 10px;
}

.card-subtitle {
  @include typography-body-large;
  margin-bottom: 10px;
}

/* Card content styles */
.card-content {
  padding: 0 $spacing-5;
  flex: 1;
}

.card-content.is-loading {
  opacity: 0.7;
  pointer-events: none;
}

/* Info row styles */
.info-row {
  display: flex;
  align-items: center;
  padding: $spacing-3 0;
  border-bottom: 1px solid var(--vt-c-white-soft);
}

.info-row:last-child {
  border-bottom: none;
}

.info-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.icon {
  @include typography-body;
}

.users-icon,
.money-icon,
.shopping-icon,
.award-icon,
.chart-icon,
.invoice-icon {
  color: var(--buttonPrimaryColor);
}

.info-label {
  @include typography-body;
  flex: 1;
  display: flex;
  align-items: center;
}

.count-badge {
  @include typography-small;
  background-color: var(--lightStandardColor);
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-md;
  border: 1px solid var(--standardColor);
  color: var(--standardColor);
  margin-left: 10px;
}

.promotion-level {
  @include typography-small;
  @include font-weight-medium;
  color: var(--vt-c-teal-dark);
  margin-left: 8px;
}

.info-value {
  @include typography-body;
  @include font-weight-semibold;
  color: #333;
  text-align: right;
}

/* Progress section styles */
.progress-section {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid var(--vt-c-white-soft);
}

.remaining-text {
  @include typography-small;
  color: #666;
  margin: 0 0 10px;
}

/* Promotion progress container and section styles */
.promotion-progress-container {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--vt-c-white-soft);
}

.promotion-progress-section {
  padding: 0;
  margin-bottom: $spacing-3;
}

.promotion-requirement-label {
  @include typography-small;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 8px;
}

.promotion-progress-message {
  @include typography-small;
  color: #666;
  margin-bottom: 10px;
}

.promotion-progress-message strong {
  @include font-weight-bold;
  color: #333;
}

.promotion-progress-completed {
  @include typography-small;
  @include font-weight-semibold;
  color: var(--greenColor);
  margin-bottom: 10px;
}

.promotion-progress-bar {
  margin-bottom: 8px;
}

.promotion-progress-bar :deep(.k-progressbar) {
  height: 8px;
  background-color: #e0e0e0;
  border-radius: $radius-sm;
}

.promotion-progress-bar :deep(.k-progressbar-fill) {
  background-color: var(--buttonPrimaryColor);
  border-radius: $radius-sm;
}

.promotion-progress-amounts {
  @include typography-small;
  @include font-weight-semibold;
  color: #333;
  text-align: right;
}

/* Carousel indicators */
.promotion-carousel-indicators {
  display: flex;
  justify-content: center;
  gap: $gap-sm;
  margin-top: 10px;
  padding-bottom: 12px;
}

.promotion-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.promotion-indicator:hover {
  background-color: #cccccc;
}

.promotion-indicator.active {
  background-color: var(--buttonPrimaryColor);
}

/* Card footer styles */
.card-footer {
  padding: $spacing-4;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #e9ecef;
  gap: $spacing-2-5;
  margin-top: auto;
}

.btn-action {
  padding: $spacing-2 $spacing-4;
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  @include typography-small;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.btn-action:hover {
  background-color: var(--vt-c-white-soft);
  border-color: #cccccc;
}

.button-icon {
  margin-right: 8px;
  color: var(--buttonPrimaryColor);
}

/* Responsive styles */
@media (max-width: 768px) {
  .dashboard-row {
    flex-direction: column;
  }
  .card-footer {
   display:block;
    padding: 0;
  }
  .card-footer button {
    width: 100%;
    margin-bottom: 5px;}
}


/* Additional styles */
.cancelButtonDiv {
  text-align: right;
}

.btnCancel,
.btnSubmitPart1,
.btnSearch,
.btnSubmitPart2 {
  background-color: white;
  border-radius: $radius-md;
  padding: $spacing-2 $spacing-8;
  @include typography-body;
  font-style: normal;
  text-align: center;
}

.btnSubmitPart1,
.btnSearch,
.btnSubmitPart2 {
  background-color: var(--vt-c-teal-dark);
  color: white;
  border-color: var(--vt-c-teal-dark);
}

.addressBookGrid .k-column-title .k-checkbox-wrap {
  display: none !important;
}

.addressBookGrid .k-checkbox {
  border: solid 1px black !important;
}

.newEventButtonDiv {
  text-align: right;
}


.radioBtnEventType {
  text-align: center;
}

.custom-label {
  display: flex !important;
  flex-direction: column-reverse; /* Reverse the order of label and radio button */
  align-items: center; /* Center the elements horizontally */
  @include typography-body;
}

.eventHostLabel {
  @include typography-body;
}

.address-val-popup-content {
  position: fixed;
  top: 50%;
  left: 50%;
  padding: $card-padding;
  transform: translate(-50%, -50%);
}

/* Error message styles */
.errorFindHost,
.errorEventDate,
.errorEventTitle,
.errorEventAddress,
.errorEventConsultant,
.errorEventHostAdd,
.errorEventType {
  color: red;
}

.eventTitleLink {
  color: var(--vt-c-teal-dark) !important;
  text-decoration: underline !important;
}

.grid-loading .k-grid-norecords .k-table-td {
  content-visibility: hidden;
  height: 50px;
}

.mvProgressHeaderDiv{
  border-top: 1px solid rgba(black, 0.2);
  border-left: 1px solid rgba(black, 0.2);
  border-right: 1px solid rgba(black, 0.2);
  margin: $spacing-1 $spacing-1 0 $spacing-1;
  border-radius: $radius-xl $radius-xl 0 0;
  padding: $spacing-4;
}

.mvProgressBodyDiv{
  border-left: 1px solid rgba(black, 0.2);
  border-right: 1px solid rgba(black, 0.2);
  border-top: 1px solid rgba(black, 0.2);
  margin: 0 $spacing-1 0 $spacing-1;
  padding: $spacing-4;
}

.mvProgressFooterDiv{
  border: 1px solid rgba(black, 0.2);
  margin: 0 $spacing-1 $spacing-1 $spacing-1;
  border-radius: 0 0 $radius-lg $radius-lg;
  padding: $spacing-4;
  text-align: right;
}

.myProgressValue{
  @include typography-h4;
  @include font-weight-bold;
  text-align: right;
  color: var(--vt-c-teal-dark);
}

.dashboardProgress {
  color: #282727;
}
</style>
